import {useContext, useRef} from "react";
import {
  LOCAL_NEW_STATE,
  LOCAL_UPDATE_STATE,
  SEND_STATE,
} from "../constants/states";
import {
  LOCAL_FORMS_AD,
  LOCAL_FORMS_AM,
  LOCAL_FORMS_OPTIONS,
  LOCAL_UPDATED_FORMS,
  LOCAL_MY_INSPECTIONS,
  LOCAL_NEW_FORMS,
  LOCAL_HELP,
  LOCAL_FORMS_P,
  LOCAL_NEW_IMAGES,
  LOCAL_UPDATED_IMAGES,
  LOCAL_ALL_INSPECTIONS,
  LOCAL_FORMS_ADMIN,
} from "../constants/local-entity";
import {
  TITLE_AGUA_DULCE,
  TITLE_AGUA_MAR,
  TITLE_PROCESOS,
  TITLE_Administracion
} from "../constants/title-forms";
import {getLocal, putLocal, dropLocal} from "../helpers/local-storage";
import {getForms} from "../mongo-db/forms";
import {getFormsOptions, saveFormsOptions} from "../mongo-db/forms-options";
import {
  deleteSavedForm,
  getSavedForm,
  getMySavedForms,
  saveSavedForms,
  getAllSavedForms,
} from "../mongo-db/saved-forms";
import {StoreContext} from "../store/StoreContext";
import _ from "lodash";
import {storeForm, updateForm} from "../util/save-forms";
import {storeImages, updateImages} from "../util/save-images";
import {getHelp} from "../mongo-db/help";
import {getImages} from "../mongo-db/images";
import {getProfiles} from "../mongo-db/profiles";

const useMain = () => {
  const formatDateView = value => {
    if (!value || typeof value !== "string") return value;
    const [datePart, timePart = ""] = value.split(" ");
    const [year, month, day] = datePart.split("-");
    const hhmm = timePart.slice(0, 5);

    if (!year || !month || !day || hhmm.length !== 5) return value;
    return `${day}-${month}-${year} ${hhmm}`;
  };

  const {
    setFormsAD,
    setFormsAM,
    setFormsP,
    setFormsADM,
    setMyInspections,
    setAllInspections,
    setFormOptions,
    setMyInspectionsPending,
    setHelp,
    setAllInspectionsPending,
    setProfiles,
  } = useContext(StoreContext);

  // Contadores de versión para cancelar respuestas obsoletas (race conditions)
  const myInspVersionRef = useRef(0);
  const allInspVersionRef = useRef(0);

  const syncFormImages = async formId => {
    const localNewImages = getLocal(LOCAL_NEW_IMAGES, []);
    const localUpdatedImages = getLocal(LOCAL_UPDATED_IMAGES, []);

    const newImg = localNewImages.find(i => i.IdForm === formId);
    const updatedImg = localUpdatedImages.find(i => i.IdForm === formId);

    if (newImg) return (await storeImages(newImg)) === true;
    if (updatedImg) return (await updateImages(formId, updatedImg)) === true;
    return true; // no hay imágenes pendientes
  };

  const syncNewForm = async form => {
    const newForm = _.omit(form, ["state"]);

    const imgOk = await syncFormImages(newForm.IdForm);
    if (!imgOk) return false;

    const result = await saveSavedForms(newForm);
    if (!result) return false;

    let newForms = getLocal(LOCAL_NEW_FORMS, []);
    newForms = newForms.filter(f => f.IdForm !== newForm.IdForm);
    putLocal(LOCAL_NEW_FORMS, newForms);
    return true;
  };

  const syncUpdatedForm = async form => {
    if (form?.state === LOCAL_NEW_STATE) {
      return syncNewForm(form);
    } else if (form?.state === LOCAL_UPDATE_STATE) {
      const newForm = _.omit(form, ["state"]);

      const imgOk = await syncFormImages(form.IdForm);
      if (!imgOk) return false;

      const savedForm = await getSavedForm(form.IdForm);
      const result = savedForm
        ? await updateForm(form.IdForm, newForm)
        : await storeForm(newForm);

      if (!result) return false;

      let newForms = getLocal(LOCAL_NEW_FORMS, []);
      let updatedForms = getLocal(LOCAL_UPDATED_FORMS, []);
      newForms = newForms.filter(f => f.IdForm !== form.IdForm);
      updatedForms = updatedForms.filter(f => f.IdForm !== form.IdForm);
      putLocal(LOCAL_NEW_FORMS, newForms);
      putLocal(LOCAL_UPDATED_FORMS, updatedForms);

      return result;
    }

    return null;
  };

  const deleteForm = async formId => {
    let insepections = getLocal(LOCAL_MY_INSPECTIONS, []);
    let newForms = getLocal(LOCAL_NEW_FORMS, []);
    let updatedForms = getLocal(LOCAL_UPDATED_FORMS, []);
    let newImages = getLocal(LOCAL_NEW_IMAGES, []);
    let updatedImages = getLocal(LOCAL_UPDATED_IMAGES, []);

    insepections = insepections.filter(i => i.IdForm !== formId);
    newForms = newForms.filter(f => f.IdForm !== formId);
    updatedForms = updatedForms.filter(f => f.IdForm !== formId);
    newImages = newImages.filter(i => i.IdForm !== formId);
    updatedImages = updatedImages.filter(i => i.IdForm !== formId);

    putLocal(LOCAL_MY_INSPECTIONS, insepections);
    putLocal(LOCAL_NEW_FORMS, newForms);
    putLocal(LOCAL_UPDATED_FORMS, updatedForms);
    putLocal(LOCAL_NEW_IMAGES, newImages);
    putLocal(LOCAL_UPDATED_IMAGES, updatedImages);

    await deleteSavedForm(formId);

    return true;
  };

  const fetchSavedForm = async formId => {
    const localInspeccitions = getLocal(LOCAL_MY_INSPECTIONS, []);
    const localNewForms = getLocal(LOCAL_NEW_FORMS, []);
    const localFormsUpdate = getLocal(LOCAL_UPDATED_FORMS, []);

    const forms = [
      ...localInspeccitions,
      ...localNewForms,
      ...localFormsUpdate,
    ];

    let form = forms.find(f => f.IdForm === formId);

    const validation =
      navigator.onLine &&
      form?.state !== LOCAL_NEW_STATE &&
      form?.state !== LOCAL_UPDATE_STATE;
    if (validation) {
      const _form = await getSavedForm(formId);
      if (_form) form = _form;
    }

    return form;
  };

  const fetchImagesByForm = async formId => {
    const localNewImages = getLocal(LOCAL_NEW_IMAGES, []);
    const localUpdatedImages = getLocal(LOCAL_UPDATED_IMAGES, []);

    const images = [...localNewImages, ...localUpdatedImages];

    let dataImages = images.find(data => data.IdForm === formId);

    const validation =
      navigator.onLine &&
      dataImages?.state !== LOCAL_NEW_STATE &&
      dataImages?.state !== LOCAL_UPDATE_STATE;
    if (validation) {
      const _dataImages = await getImages(formId);
      if (_dataImages) dataImages = _dataImages;
    }

    return dataImages;
  };

  const fetchForms = async () => {
    let ad = getLocal(LOCAL_FORMS_AD, []);
    let am = getLocal(LOCAL_FORMS_AM, []);
    let p = getLocal(LOCAL_FORMS_P, []);
    let adm = getLocal(LOCAL_FORMS_ADMIN, []);

    setFormsAD(ad);
    setFormsAM(am);
    setFormsP(p);
    setFormsADM(adm)

    if (navigator.onLine) {
      const forms = await getForms();
      if (forms.length <= 0) return;

      ad = forms.filter(({Titulo}) => Titulo?.includes(TITLE_AGUA_DULCE));
      am = forms.filter(({Titulo}) => Titulo?.includes(TITLE_AGUA_MAR));
      p = forms.filter(({Titulo}) => Titulo?.includes(TITLE_PROCESOS));
      adm = forms.filter(({Titulo}) => Titulo?.includes(TITLE_Administracion));

      if (ad.length > 0) {
        putLocal(LOCAL_FORMS_AD, ad);
        setFormsAD(ad);
      }
      if (am.length > 0) {
        putLocal(LOCAL_FORMS_AM, am);
        setFormsAM(am);
      }
      if (p.length > 0) {
        putLocal(LOCAL_FORMS_P, p);
        setFormsP(p);
      }
      if (adm.length > 0) {
        putLocal(LOCAL_FORMS_ADMIN, adm);
        setFormsADM(adm);
      }
    }
  };

  const fetchSavedForms = async (username, options = {}) => {
    const version = ++myInspVersionRef.current;
    const page = Math.max(1, Number(options?.page) || 1);
    const limit = Math.max(1, Number(options?.limit) || 20);
    const sortBy = options?.sortBy || options?.sortField || "FechaFormulario";
    const sortDir = options?.sortDir === "asc" ? "asc" : "desc";
    const search = (options?.search || "").trim();
    const contentFilter = options?.contentFilter || "";
    const centerName = options?.centerName || "";
    const fromDate = options?.fromDate || "";
    const toDate = options?.toDate || "";

    setMyInspectionsPending(true);
    const localNewForms = getLocal(LOCAL_NEW_FORMS, []);
    const localFormsToUpdate = getLocal(LOCAL_UPDATED_FORMS, []);

    let pagination = { page, limit, total: 0, totalPages: 0, hasMore: false };

    try {
      if (navigator.onLine) {
        const response = await getMySavedForms(
          username, page, limit, sortBy, sortDir, search, contentFilter,
          { centerName, fromDate, toDate }
        );
        // Ignorar respuesta si ya hay una petición más reciente
        if (version !== myInspVersionRef.current) return null;

        let _myInspections = _.get(response, "data", []);
        const backendPagination = _.get(response, "pagination", {});
        const safePage = Number(backendPagination.page) || page;
        const safeLimit = Number(backendPagination.limit) || limit;
        const safeTotal = Number(backendPagination.total) || 0;

        _myInspections = _myInspections.map(i => ({
          ...i,
          FechaFormularioRaw: i.FechaFormulario,
          FechaFormulario: formatDateView(i.FechaFormulario),
        }));

        const serverForms = _.uniqBy(_myInspections, "IdForm").map(insp => ({
          ...insp,
          state: !insp.state ? SEND_STATE : insp.state,
        }));
        const serverIds = new Set(serverForms.map(sf => sf.IdForm));
        const pendingForms = [...localNewForms, ...localFormsToUpdate].filter(
          lf => !serverIds.has(lf.IdForm)
        );
        const all = [...pendingForms, ...serverForms];
        setMyInspections(all);

        pagination = {
          page: safePage,
          limit: safeLimit,
          total: safeTotal + pendingForms.length,
          totalPages: Math.ceil((safeTotal + pendingForms.length) / safeLimit),
          hasMore: safePage * safeLimit < safeTotal,
        };
      } else {
        const _localMyInspections = getLocal(LOCAL_MY_INSPECTIONS, []);
        const localAll = [
          ..._localMyInspections,
          ...localNewForms,
          ...localFormsToUpdate,
        ].map(insp => ({
          ...insp,
          state: !insp.state ? SEND_STATE : insp.state,
        }));
        setMyInspections(localAll);
        pagination = {
          page,
          limit,
          total: localAll.length,
          totalPages: Math.ceil(localAll.length / limit),
          hasMore: page * limit < localAll.length,
        };
      }
    } finally {
      if (version === myInspVersionRef.current) {
        setMyInspectionsPending(false);
      }
    }

    return pagination;
  };

  const fetchAllSavedForms = async (options = {}) => {
    const version = ++allInspVersionRef.current;
    const page = Math.max(1, Number(options?.page) || 1);
    const limit = Math.max(1, Number(options?.limit) || 20);
    const sortBy = options?.sortBy || options?.sortField || "FechaFormulario";
    const sortDir = options?.sortDir === "asc" ? "asc" : "desc";
    const search = (options?.search || "").trim();
    const contentFilter = options?.contentFilter || "";
    const centerName = options?.centerName || "";
    const centerNames = options?.centerNames || "";
    const fromDate = options?.fromDate || "";
    const toDate = options?.toDate || "";

    setAllInspectionsPending(true);

    let pagination = { page, limit, total: 0, totalPages: 0, hasMore: false };

    try {
      if (navigator.onLine) {
        const response = await getAllSavedForms(page, limit, sortBy, sortDir, search, contentFilter, { centerName, centerNames, fromDate, toDate });
        // Ignorar respuesta si ya hay una petición más reciente
        if (version !== allInspVersionRef.current) return null;

        let _allInspections = _.get(response, "data", null);
        const backendPagination = _.get(response, "pagination", {});
        const safePage = Number(backendPagination.page) || page;
        const safeLimit = Number(backendPagination.limit) || limit;
        const safeTotal = Number(backendPagination.total) || 0;

        if (!Array.isArray(_allInspections)) _allInspections = Array.isArray(response) ? response : [];

        _allInspections = _allInspections.map(i => ({
          ...i,
          FechaFormularioRaw: i.FechaFormulario,
          FechaFormulario: formatDateView(i.FechaFormulario),
        }));

        const all = _.uniqBy(_allInspections, "IdForm").map(insp => ({
          ...insp,
          state: !insp.state ? SEND_STATE : insp.state,
        }));
        setAllInspections(all);

        const resolvedTotal = safeTotal || all.length;
        pagination = {
          page: safePage,
          limit: safeLimit,
          total: resolvedTotal,
          totalPages: Math.ceil(resolvedTotal / safeLimit),
          hasMore: safePage * safeLimit < resolvedTotal,
        };
      } else {
        const _localAllInspections = getLocal(LOCAL_ALL_INSPECTIONS, []);
        const local = _localAllInspections.map(insp => ({
          ...insp,
          state: !insp.state ? SEND_STATE : insp.state,
        }));
        setAllInspections(local);
        pagination = {
          page,
          limit,
          total: local.length,
          totalPages: Math.ceil(local.length / limit),
          hasMore: page * limit < local.length,
        };
      }
    } finally {
      if (version === allInspVersionRef.current) {
        setAllInspectionsPending(false);
      }
    }

    return pagination;
  };

  const fetchFormOptionsOnline = async () => {
    const options = await getFormsOptions();
    if (!_.isEmpty(options)) putLocal(LOCAL_FORMS_OPTIONS, options);
    return options;
  };

  const fetchHelp = async () => {
    const help = getLocal(LOCAL_HELP, []);
    setHelp(help);

    if (navigator.onLine) {
      const _help = await getHelp();
      if (!_.isEmpty(_help)) {
        putLocal(LOCAL_HELP, _help);
        setHelp(_help);
      }
    }
  };

  const storeFormOptions = async (key, values) => {
    const options = getLocal(LOCAL_FORMS_OPTIONS, {});
    options[key] = values;
    await saveFormsOptions(options.optionId, options);
    putLocal(LOCAL_FORMS_OPTIONS, options);
    setFormOptions(options);
    return options;
  };

  const syncAllPending = async () => {
    const localNewForms = getLocal(LOCAL_NEW_FORMS, []);
    const localUpdatedForms = getLocal(LOCAL_UPDATED_FORMS, []);
    let synced = 0;

    for (const form of localNewForms) {
      const ok = await syncNewForm(form);
      if (ok) synced++;
    }
    for (const form of localUpdatedForms) {
      const ok = await syncUpdatedForm(form);
      if (ok) synced++;
    }

    // Imágenes huérfanas (sin formulario pendiente asociado)
    const remainingNewForms = getLocal(LOCAL_NEW_FORMS, []);
    const remainingUpdatedForms = getLocal(LOCAL_UPDATED_FORMS, []);
    const pendingFormIds = new Set([
      ...remainingNewForms.map(f => f.IdForm),
      ...remainingUpdatedForms.map(f => f.IdForm),
    ]);

    let cleanedNewImages = getLocal(LOCAL_NEW_IMAGES, []);
    for (const imgData of [...cleanedNewImages]) {
      if (pendingFormIds.has(imgData.IdForm)) continue;
      const ok = await storeImages(imgData);
      if (ok === true) {
        cleanedNewImages = cleanedNewImages.filter(i => i.IdForm !== imgData.IdForm);
      }
    }
    putLocal(LOCAL_NEW_IMAGES, cleanedNewImages);

    let cleanedUpdatedImages = getLocal(LOCAL_UPDATED_IMAGES, []);
    for (const imgData of [...cleanedUpdatedImages]) {
      if (pendingFormIds.has(imgData.IdForm)) continue;
      const ok = await updateImages(imgData.IdForm, imgData);
      if (ok === true) {
        cleanedUpdatedImages = cleanedUpdatedImages.filter(i => i.IdForm !== imgData.IdForm);
      }
    }
    putLocal(LOCAL_UPDATED_IMAGES, cleanedUpdatedImages);

    return synced;
  };

  const fetchProfiles = () => {
    return getProfiles().then(res => {
      const profilesAux = res.map(p => ({id: Number(p.id), title: p.title}));
      setProfiles(profilesAux);
    });
  };

  const start = async (username, profile) => {
    fetchProfiles();
    fetchForms();
    if (profile !== 1 && profile !== 2) {
      dropLocal(LOCAL_ALL_INSPECTIONS);
    }
    dropLocal(LOCAL_FORMS_OPTIONS); // Limpiar cache legacy de formOptions
    fetchHelp();
  };

  return {
    start,
    fetchSavedForms,
    fetchSavedForm,
    syncNewForm,
    syncUpdatedForm,
    deleteForm,
    fetchHelp,
    fetchImagesByForm,
    fetchAllSavedForms,
    storeFormOptions,
    fetchFormOptionsOnline,
    syncAllPending,
    fetchProfiles,

  };
};

export default useMain;
